// EnhancedHotelBookingForm.jsx
import React, { useState, useEffect } from "react";
import { format, addDays, isBefore, isAfter } from "date-fns";
import Select from "react-select";
import { hotelBookingAPI } from '../api/bookingApi';
import { toast } from 'react-toastify';

const EnhancedHotelBookingForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    checkIn: format(addDays(new Date(), 1), "yyyy-MM-dd"),
    checkOut: format(addDays(new Date(), 2), "yyyy-MM-dd"),
    guests: 1,
    roomType: "standard",
    extraBed: false,
    mealPlan: "breakfast",
    specialRequests: "",
    // New backend fields
    bookingCode: "",
    status: "",
    audit: {},
    refund: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [bookingResult, setBookingResult] = useState(null);

  const roomTypes = {
    standard: { name: "Standard", price: 100, maxOccupancy: 2 },
    deluxe: { name: "Deluxe", price: 150, maxOccupancy: 3 },
    suite: { name: "Suite", price: 250, maxOccupancy: 4 },
    family: { name: "Family", price: 200, maxOccupancy: 6 }
  };

  const countries = [
    "United States",
    "United Kingdom",
    "India",
    "Canada",
    "Australia",
    "France",
    "Germany",
    "Japan"
  ];

  useEffect(() => {
    calculateTotal();
  }, [formData]);

  const calculateTotal = () => {
    const checkInDate = new Date(formData.checkIn);
    const checkOutDate = new Date(formData.checkOut);
    const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));

    let total = roomTypes[formData.roomType].price * nights;
    if (formData.extraBed) total += 50 * nights;
    if (formData.mealPlan === "fullboard") total += 40 * nights * formData.guests;
    if (formData.mealPlan === "halfboard") total += 25 * nights * formData.guests;

    setTotalPrice(total);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format";

    const checkInDate = new Date(formData.checkIn);
    const checkOutDate = new Date(formData.checkOut);
    const today = new Date();

    if (isBefore(checkInDate, today)) newErrors.checkIn = "Check-in date cannot be in the past";
    if (!isAfter(checkOutDate, checkInDate)) newErrors.checkOut = "Check-out must be after check-in";
    if (formData.guests > roomTypes[formData.roomType].maxOccupancy) newErrors.guests = "Exceeds max occupancy";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      // Prepare payload for backend
      const payload = {
        ...formData,
        totalAmount: totalPrice,
        // Add any other required backend fields here
      };
      hotelBookingAPI.createBookingWithPayment(payload)
        .then((result) => {
          setIsSubmitting(false);
          setBookingResult(result);
          setShowModal(true);
        })
        .catch((err) => {
          setIsSubmitting(false);
          toast.error("Booking failed: " + err.message);
        });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-6 text-center">Book Your Hotel Room</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Guest Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">First Name *</label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className={`w-full mt-1 p-2 border rounded-md ${errors.firstName ? 'border-red-100' : 'border-gray-300'}`} />
              {errors.firstName && <p className="text-sm text-red-600">{errors.firstName}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Last Name *</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className={`w-full mt-1 p-2 border rounded-md ${errors.lastName ? 'border-red-100' : 'border-gray-300'}`} />
              {errors.lastName && <p className="text-sm text-red-600">{errors.lastName}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={`w-full mt-1 p-2 border rounded-md ${errors.email ? 'border-red-100' : 'border-gray-300'}`} />
              {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full mt-1 p-2 border border-gray-300 rounded-md" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium">Country</label>
              <Select
                options={countries.map(c => ({ label: c, value: c }))}
                value={{ label: formData.country, value: formData.country }}
                onChange={(opt) => setFormData(prev => ({ ...prev, country: opt.value }))}
              />
            </div>
          </div>

          {/* Booking Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Check-in *</label>
              <input type="date" name="checkIn" value={formData.checkIn} onChange={handleInputChange} className={`w-full mt-1 p-2 border rounded-md ${errors.checkIn ? 'border-red-100' : 'border-gray-300'}`} />
              {errors.checkIn && <p className="text-sm text-red-600">{errors.checkIn}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Check-out *</label>
              <input type="date" name="checkOut" value={formData.checkOut} onChange={handleInputChange} className={`w-full mt-1 p-2 border rounded-md ${errors.checkOut ? 'border-red-100' : 'border-gray-300'}`} />
              {errors.checkOut && <p className="text-sm text-red-600">{errors.checkOut}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Guests</label>
              <select name="guests" value={formData.guests} onChange={handleInputChange} className={`w-full mt-1 p-2 border rounded-md ${errors.guests ? 'border-red-100' : 'border-gray-300'}`}>
                {[1, 2, 3, 4, 5, 6].map(num => <option key={num} value={num}>{num}</option>)}
              </select>
              {errors.guests && <p className="text-sm text-red-600">{errors.guests}</p>}
            </div>
            <div>
              <label className="text-sm font-medium">Room Type</label>
              <select name="roomType" value={formData.roomType} onChange={handleInputChange} className="w-full mt-1 p-2 border border-gray-300 rounded-md">
                {Object.entries(roomTypes).map(([key, room]) => (
                  <option key={key} value={key}>{room.name} (${room.price}/night)</option>
                ))}
              </select>
            </div>
          </div>

          {/* Extras */}
          <div className="space-y-2">
            <div className="flex items-center">
              <input type="checkbox" name="extraBed" checked={formData.extraBed} onChange={handleInputChange} className="mr-2" />
              <span>Add Extra Bed (+$50/night)</span>
            </div>
            <div>
              <label className="text-sm font-medium">Meal Plan</label>
              <select name="mealPlan" value={formData.mealPlan} onChange={handleInputChange} className="w-full mt-1 p-2 border border-gray-300 rounded-md">
                <option value="breakfast">Breakfast Only</option>
                <option value="halfboard">Half Board (+$25/guest/day)</option>
                <option value="fullboard">Full Board (+$40/guest/day)</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Special Requests</label>
              <textarea name="specialRequests" value={formData.specialRequests} onChange={handleInputChange} rows="3" className="w-full mt-1 p-2 border border-gray-300 rounded-md"></textarea>
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <p className="text-lg font-semibold">Total Price: ${totalPrice}</p>
            <p className="text-sm text-gray-100">All inclusive</p>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4">
            <button type="button" onClick={() => window.location.reload()} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Reset</button>
            <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60">
              {isSubmitting ? "Processing..." : "Book Now"}
            </button>
          </div>
        </form>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-bold mb-2">Booking Confirmed</h3>
            <p className="text-sm text-gray-700 mb-2">A confirmation has been sent to {formData.email}</p>
            {bookingResult && (
              <div className="mb-2">
                <p><b>Booking Code:</b> {bookingResult.bookingCode}</p>
                <p><b>Status:</b> {bookingResult.status}</p>
                <p><b>Audit Info:</b> {bookingResult.audit ? JSON.stringify(bookingResult.audit) : 'N/A'}</p>
                {bookingResult.refund && <p><b>Refund:</b> {JSON.stringify(bookingResult.refund)}</p>}
              </div>
            )}
            <button onClick={() => window.location.reload()} className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedHotelBookingForm;
